import { useCallback, useState, useEffect, useMemo } from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { cloneDeep } from 'lodash';
import type { Dispatch, SetStateAction } from 'react';
import type { ReadonlyDeep, Writable } from 'type-fest';

import { BooleanFilter, ensureDefined, isDefined } from '@e-socna/utils';
import { EMPTY_ARRAY, EMPTY_OBJECT, EMPTY_STRING } from '@e-socna/empty';
import type { Id } from '@e-socna/data-layer';
import type { ContentOriginId, SectionTypeId } from '@e-socna/data-layer';

import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useGraphQLQueries } from '../../../hooks/useGraphQLQueries.hook';

import { customLayoutsPriority } from '../utils/custom-layouts-priority.util';
import { DocumentEditorCmp_OneTemplateDependency_QueryDocument } from '../queries/one-template-dependency.query';
import { getVirtualRoot } from '../utils/get-virtual-root.util';
import type { EnrichedSection } from '../types/enriched-section.type';
import type { DocumentEditorState } from '../types/editor-state.type';
import type { DocumentEditorCmp_CustomLayout } from '../fragments/custom-layout.fragment';
import type {
  DocumentEditorCmp_RecursiveSection,
  DocumentEditorCmp_Section,
} from '../fragments/recursive-section.fragment';
import type { DocumentEditorCmp_TemplateDependency } from '../queries/one-template-dependency.query';
import type { Editable } from '../types/editable.type';
import type { ContentConstraints, Getters } from '../types/props.type';

type Accumulator = ReadonlyDeep<
  Readonly<{
    sections: readonly EnrichedSection[];
    displayPath: readonly number[];
    realPath: readonly number[];
    alreadyUsedIds: readonly Id[];
    insertionPoint?: {
      id: Id;
      realPath: readonly number[];
      contentOriginId: ContentOriginId;
    };
    customLayouts: readonly DocumentEditorCmp_CustomLayout[];
    noSiblings?: boolean;
    forcePagebreaks?: boolean;
    typeId: SectionTypeId;
    isFullyLoaded: boolean;
  }>
>;

interface Params<E extends Editable> {
  readonly state: DocumentEditorState<E>;
  readonly templateDependencies: readonly DocumentEditorCmp_TemplateDependency[];
  readonly setTemplateDependencies: Dispatch<
    SetStateAction<readonly DocumentEditorCmp_TemplateDependency[]>
  >;
  readonly contentConstraints?: ContentConstraints;
}

interface SectionParams<E extends Editable> extends Params<E> {
  readonly section: ReadonlyDeep<DocumentEditorCmp_RecursiveSection>;
}

interface VirtualRootParams<E extends Editable> extends Params<E> {
  readonly getters: Getters<E>;
}

type Template = NonNullable<DocumentEditorCmp_RecursiveSection['template']>;

// MHO Please read
// https://reactjs.org/docs/hooks-faq.html#how-to-get-the-previous-props-or-state
// https://github.com/facebook/react/issues/14476
// https://usehooks.com/useMemoCompare/
// https://stackoverflow.com/questions/53070970/infinite-loop-in-useeffect
// https://stackoverflow.com/questions/54095994/react-useeffect-comparing-objects
// https://blog.logrocket.com/solve-react-useeffect-hook-infinite-loop-patterns/#using-an-array-as-a-dependency
// https://github.com/TanStack/query/issues/3784
const useEnrichSection = <E extends Editable>({
  section,
  state,
  templateDependencies,
  setTemplateDependencies,
  contentConstraints,
}: Readonly<SectionParams<E>>): Readonly<EnrichedSection> => {
  const [templateReferences, setTemplateReferences] =
    useState<readonly Template[]>(EMPTY_ARRAY);

  const { contentOriginsCodeMap, sectionTypesIdMap, sectionTypesCodeMap } =
    useReferencesContext();

  const grabDependencies = useCallback(
    (
      section: ReadonlyDeep<DocumentEditorCmp_RecursiveSection>,
    ): readonly Template[] => {
      const recursiveGrabber = (
        section: ReadonlyDeep<DocumentEditorCmp_RecursiveSection>,
        pGrabbed?: readonly Template[],
      ): readonly Template[] => {
        const grabbed = (
          section.subSections as readonly DocumentEditorCmp_RecursiveSection[]
        )
          .reduce(
            (subPreviousAcc, subSection) =>
              recursiveGrabber(
                subSection as ReadonlyDeep<DocumentEditorCmp_RecursiveSection>,
                subPreviousAcc,
              ),
            section.template
              ? [...(pGrabbed ?? EMPTY_ARRAY), section.template]
              : (pGrabbed ?? EMPTY_ARRAY),
          )
          .filter(BooleanFilter);

        const deduper = (
          item: Readonly<Template>,
          index: number,
          array: readonly Template[],
        ) =>
          array.findIndex((otherItem) => otherItem._id === item._id) === index;

        const dedupedGrabbed = grabbed.filter(deduper);

        if (!pGrabbed) {
          setTemplateReferences((oldDependencies) =>
            [...oldDependencies, ...dedupedGrabbed]
              .filter(BooleanFilter)
              .filter(deduper),
          );
        }

        return dedupedGrabbed;
      };

      return recursiveGrabber(section);
    },
    [],
  );

  useEffect(() => {
    grabDependencies(section);
  }, [section, grabDependencies]);

  useDeepCompareEffect(() => {
    templateReferences.forEach((reference) => {
      const template = templateDependencies.find(
        (template) => template._id === reference._id,
      );

      if (!template) {
        return;
      }

      const templateVirtualRoot = getVirtualRoot(template);

      grabDependencies(
        templateVirtualRoot as DocumentEditorCmp_RecursiveSection,
      );
    });
  }, [templateReferences, templateDependencies]);

  const results = useGraphQLQueries(
    templateReferences.map((reference) => ({
      document: DocumentEditorCmp_OneTemplateDependency_QueryDocument,
      variables: { id: reference._id, type: reference.type },
      useQueryOptions: { enabled: !!reference },
    })),
  );

  useDeepCompareEffect(() => {
    results.forEach(({ data }) => {
      if (!data?.findOneTemplate) {
        return;
      }

      const template = data.findOneTemplate;

      setTemplateDependencies((previousTemplateDependencies) => {
        const newTemplateDependencies = cloneDeep(
          previousTemplateDependencies,
        ) as Writable<typeof previousTemplateDependencies>;

        const index = newTemplateDependencies.findIndex(
          (dependency) => dependency._id === template._id,
        );

        if (index === -1) {
          newTemplateDependencies.push(template);
        } else {
          newTemplateDependencies[index] = template;
        }

        return newTemplateDependencies;
      });
    });
  }, [results]);

  const enrichSection = useCallback(
    (
      previousValue: Accumulator,
      currentValue: ReadonlyDeep<DocumentEditorCmp_RecursiveSection>,
    ): Accumulator => {
      let enrichedSection: Writable<EnrichedSection> | null = null;

      let lastDisplayPath: readonly number[] | null = null;

      const alreadyUsedIds = [
        ...previousValue.alreadyUsedIds,
        currentValue._id,
        ...(currentValue.template ? [currentValue.template._id] : EMPTY_ARRAY),
      ];

      let tmplt;
      let oneChildMax = false;
      let forcePagebreaks = false;
      let typeId = previousValue.typeId;
      let isFullyLoaded = previousValue.isFullyLoaded;

      if (currentValue.template) {
        tmplt = templateDependencies.find(
          (t) => t._id === currentValue.template?._id,
        );

        if (tmplt) {
          switch (tmplt.__typename) {
            case 'SectionTemplate': {
              const { sectionType } = ensureDefined({
                sectionType: sectionTypesIdMap[tmplt.typeId as SectionTypeId],
              });

              if (currentValue.isInsertionPoint) {
                oneChildMax = sectionType.oneChildMax;
                forcePagebreaks = sectionType.forcePagebreaks;
              }

              typeId = tmplt.typeId as SectionTypeId;

              break;
            }
            case 'DocumentTemplate': {
              typeId = sectionTypesCodeMap.Normal._id;
              oneChildMax = false;
              forcePagebreaks = false;

              break;
            }
            default: {
              throw new Error('Unsupported template typename');
            }
          }
        } else {
          isFullyLoaded = false;
        }
      }

      if (!oneChildMax && previousValue.noSiblings && currentValue.isVirtual) {
        oneChildMax = true;
      }

      // Enrich a catalog content
      if (
        currentValue.isVirtual &&
        currentValue.template &&
        currentValue.contentOriginId === contentOriginsCodeMap.Catalog._id
      ) {
        const firstPassCustomLayouts = customLayoutsPriority(
          previousValue.customLayouts,
          currentValue.customLayouts ?? EMPTY_ARRAY,
        );

        if (tmplt) {
          const tmpltVirtualRoot = getVirtualRoot(tmplt);

          const secondPassCustomLayouts = customLayoutsPriority(
            firstPassCustomLayouts,
            tmpltVirtualRoot.customLayouts ?? EMPTY_ARRAY,
          );

          const currentValueCustomLayout = secondPassCustomLayouts.find(
            (cl) => cl.sectionId === currentValue._id,
          );

          const children = (
            tmpltVirtualRoot.subSections as readonly DocumentEditorCmp_RecursiveSection[]
          )
            // TODO : check if we can refactor this code in order to respect this rule
            // eslint-disable-next-line react-hooks/immutability
            .reduce(enrichSection, {
              sections: EMPTY_ARRAY,
              displayPath: previousValue.displayPath,
              realPath: [...previousValue.realPath, 0],
              alreadyUsedIds,
              insertionPoint: currentValue.isInsertionPoint
                ? {
                    id: currentValue._id,
                    realPath: previousValue.realPath,
                    contentOriginId:
                      currentValue.contentOriginId as ContentOriginId,
                  }
                : previousValue.insertionPoint,
              customLayouts: secondPassCustomLayouts,
              ...(oneChildMax ? { noSiblings: true } : EMPTY_OBJECT),
              typeId,
              isFullyLoaded,
            });

          isFullyLoaded = isFullyLoaded && children.isFullyLoaded;
          lastDisplayPath = children.displayPath;

          enrichedSection = {
            ...(tmpltVirtualRoot as DocumentEditorCmp_Section),
            ...currentValue,
            title: currentValue.title ?? tmpltVirtualRoot.title,
            content: currentValue.content ?? tmpltVirtualRoot.content,
            realPath: previousValue.realPath,
            alreadyUsedIds,
            ...(!currentValue.isInsertionPoint
              ? { insertionPoint: previousValue.insertionPoint }
              : EMPTY_OBJECT),
            ...(previousValue.insertionPoint && currentValueCustomLayout
              ? {
                  orientation: !isDefined(currentValueCustomLayout.orientation)
                    ? currentValue.orientation
                    : currentValueCustomLayout.orientation,
                  paperFormat: !isDefined(currentValueCustomLayout.paperFormat)
                    ? currentValue.paperFormat
                    : currentValueCustomLayout.paperFormat,
                  hasPagebreakAfter: !isDefined(
                    currentValueCustomLayout.hasPagebreakAfter,
                  )
                    ? currentValue.hasPagebreakAfter
                    : currentValueCustomLayout.hasPagebreakAfter,
                  hasPagebreakBefore: !isDefined(
                    currentValueCustomLayout.hasPagebreakBefore,
                  )
                    ? currentValue.hasPagebreakBefore
                    : currentValueCustomLayout.hasPagebreakBefore,
                }
              : EMPTY_OBJECT),
            subSections: children.sections,
            typeId,
            isFullyLoaded,
          };
        } else {
          // section is loading
          enrichedSection = {
            ...currentValue,
            realPath: previousValue.realPath,
            alreadyUsedIds,
            isVirtual: true,
            isLoading: true,
            subSections: EMPTY_ARRAY,
            typeId,
            isFullyLoaded: false,
          };
        }
      } else {
        // other cases
        let customLayout;

        if (
          previousValue.insertionPoint?.contentOriginId ===
          contentOriginsCodeMap.Catalog._id
        ) {
          customLayout = previousValue.customLayouts.find(
            (customLayout) => customLayout.sectionId === currentValue._id,
          );
        }

        const children: Accumulator = (
          currentValue.subSections as readonly ReadonlyDeep<DocumentEditorCmp_RecursiveSection>[]
        ).reduce(enrichSection, {
          sections: EMPTY_ARRAY,
          displayPath:
            currentValue.isVirtual ||
            !currentValue.title ||
            currentValue.title === EMPTY_STRING
              ? previousValue.displayPath
              : [...previousValue.displayPath, 0],
          realPath: [...previousValue.realPath, 0],
          alreadyUsedIds,
          insertionPoint: previousValue.insertionPoint,
          customLayouts: previousValue.customLayouts,
          ...(oneChildMax ? { noSiblings: true } : EMPTY_OBJECT),
          typeId,
          isFullyLoaded,
        });

        isFullyLoaded = isFullyLoaded && children.isFullyLoaded;
        lastDisplayPath = children.displayPath;

        enrichedSection = {
          ...currentValue,
          ...(customLayout
            ? {
                orientation: !isDefined(customLayout.orientation)
                  ? currentValue.orientation
                  : customLayout.orientation,
                paperFormat: !isDefined(customLayout.paperFormat)
                  ? currentValue.paperFormat
                  : customLayout.paperFormat,
                hasPagebreakAfter: !isDefined(customLayout.hasPagebreakAfter)
                  ? currentValue.hasPagebreakAfter
                  : customLayout.hasPagebreakAfter,
                hasPagebreakBefore: !isDefined(customLayout.hasPagebreakBefore)
                  ? currentValue.hasPagebreakBefore
                  : customLayout.hasPagebreakBefore,
              }
            : EMPTY_OBJECT),
          realPath: previousValue.realPath,
          alreadyUsedIds,
          ...(!currentValue.isVirtual && currentValue.title
            ? { displayPath: previousValue.displayPath }
            : EMPTY_OBJECT),
          insertionPoint: previousValue.insertionPoint,
          isFullyLoaded,
          subSections: children.sections,
          typeId,
        };
      }

      if (previousValue.noSiblings) {
        enrichedSection.noSiblings = true;
      }

      if (previousValue.forcePagebreaks || forcePagebreaks) {
        enrichedSection.hasPagebreakBefore = true;
        enrichedSection.hasPagebreakAfter = true;
        enrichedSection.hasLockedPagebreaks = true;
      }

      enrichedSection.isFolded = state.foldedSectionIds.includes(
        enrichedSection._id,
      );

      enrichedSection.oneChildMax = oneChildMax;

      return {
        sections: [...previousValue.sections, enrichedSection],
        displayPath: !lastDisplayPath
          ? previousValue.displayPath
          : previousValue.displayPath.length === lastDisplayPath.length
            ? lastDisplayPath
            : lastDisplayPath
                .slice(0, previousValue.displayPath.length)
                .map((step, idx, arr) =>
                  idx === arr.length - 1 ? step + 1 : step,
                ),
        realPath: previousValue.realPath.map((step, idx, arr) =>
          idx === arr.length - 1 ? step + 1 : step,
        ),
        alreadyUsedIds: previousValue.alreadyUsedIds,
        insertionPoint: previousValue.insertionPoint,
        customLayouts: previousValue.customLayouts,
        ...(previousValue.noSiblings ? { noSiblings: true } : EMPTY_OBJECT),
        typeId: previousValue.typeId,
        isFullyLoaded,
      };
    },
    [
      contentOriginsCodeMap.Catalog._id,
      state.foldedSectionIds,
      sectionTypesIdMap,
      sectionTypesCodeMap.Normal._id,
      templateDependencies,
    ],
  );

  const enrichedContent = useMemo<EnrichedSection>(() => {
    const rootIds = [state.editable._id, section._id];

    const enrichedSubSections: Accumulator = (
      [section] as readonly DocumentEditorCmp_RecursiveSection[]
    ).reduce<Accumulator>(enrichSection, {
      sections: EMPTY_ARRAY,
      displayPath: [0],
      realPath: EMPTY_ARRAY,
      alreadyUsedIds: rootIds,
      customLayouts: EMPTY_ARRAY,
      ...(contentConstraints?.noSiblings ? { noSiblings: true } : EMPTY_OBJECT),
      ...(contentConstraints?.forcePagebreaks
        ? { forcePagebreaks: true }
        : EMPTY_OBJECT),
      typeId: sectionTypesCodeMap.Normal._id,
      isFullyLoaded: true,
    });

    return {
      typeId: sectionTypesCodeMap.Normal._id,
      ...section,
      realPath: EMPTY_ARRAY,
      alreadyUsedIds: rootIds,
      subSections: enrichedSubSections.sections,
      isFullyLoaded: enrichedSubSections.isFullyLoaded,
    };
  }, [
    section,
    state.editable._id,
    enrichSection,
    contentConstraints?.forcePagebreaks,
    contentConstraints?.noSiblings,
    sectionTypesCodeMap.Normal._id,
  ]);

  return enrichedContent;
};

export const useEnrichVirtualRoot = <E extends Editable>(
  params: Readonly<VirtualRootParams<E>>,
): EnrichedSection => {
  const {
    state,
    getters: { editable: getEditable },
  } = params;

  const section = useMemo(
    () => getEditable(state.editable).virtualRoot,
    [state.editable, getEditable],
  );

  const enrichedContent = useEnrichSection<E>({ section, ...params });

  const unnestVirtualRoot = useCallback(
    (enrichedSection: Readonly<EnrichedSection>) => ({
      ...enrichedSection,
      subSections: enrichedSection.subSections[0]?.subSections ?? EMPTY_ARRAY,
    }),
    [],
  );

  return unnestVirtualRoot(enrichedContent);
};
